import { audio } from '../../../../../../audio/AudioManager.js';
import { NUMBER_KINGDOM_ASSETS } from '../../../NumberKingdomAssets.js';
import type { LogicalBounds } from '../../../../../shared/layout/index.js';
import { ADDITION_CITADEL_ASSETS } from '../AdditionCitadelAssets.js';
import { TwinTowerCombine } from './TwinTowerCombine.js';
import type { TwinTowerCombineConfig, TwinTowerId } from './TwinTowerCombineConfig.js';
import type { TwinTowerCombineSnapshot } from './TwinTowerCombineState.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

export interface TwinTowerCombineViewOptions {
  readonly reducedMotion: boolean;
  readonly onComplete: (snapshot: TwinTowerCombineSnapshot) => void;
}

type UnitPlacement = {
  readonly image: Phaser.GameObjects.Image;
  readonly tower: TwinTowerId;
  readonly index: number;
};

/**
 * Production-art view for the rendering-independent TwinTowerCombine engine.
 * Curriculum values come only from config/state; this layer owns input,
 * animation, responsive placement, and accessible visual feedback.
 */
export class TwinTowerCombineView {
  readonly controller: TwinTowerCombine;

  private readonly root: Phaser.GameObjects.Container;
  private readonly missionPanel: Phaser.GameObjects.Image;
  private readonly leftTower: Phaser.GameObjects.Image;
  private readonly rightTower: Phaser.GameObjects.Image;
  private readonly leftLabel: Phaser.GameObjects.Text;
  private readonly rightLabel: Phaser.GameObjects.Text;
  private readonly equation: Phaser.GameObjects.Text;
  private readonly instruction: Phaser.GameObjects.Text;
  private readonly hintButton: Phaser.GameObjects.Image;
  private readonly resetButton: Phaser.GameObjects.Image;
  private readonly hintLabel: Phaser.GameObjects.Text;
  private readonly resetLabel: Phaser.GameObjects.Text;
  private readonly transferZone: Phaser.GameObjects.Zone;
  private readonly unitPlacements: UnitPlacement[] = [];
  private safe?: LogicalBounds;
  private compact = false;
  private suspended = false;
  private moving = false;
  private destroyed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    config: TwinTowerCombineConfig,
    private readonly options: TwinTowerCombineViewOptions,
  ) {
    this.controller = new TwinTowerCombine(config, {
      onStateChanged: snapshot => this.renderState(snapshot),
      onComplete: (_completedConfig, snapshot) => options.onComplete(snapshot),
    });

    this.root = scene.add.container(0, 0).setDepth(30);
    this.missionPanel = scene.add.image(640, 96, ADDITION_CITADEL_ASSETS.missionPanel.key)
      .setCrop(145, 145, 750, 215).setOrigin(520 / 900, 252.5 / 363);
    this.leftTower = scene.add.image(430, 420, ADDITION_CITADEL_ASSETS.twinTowerLeft.key)
      .setCrop(100, 130, 620, 850).setOrigin(410 / 768, 555 / 1024).setAlpha(0.88);
    this.rightTower = scene.add.image(850, 420, ADDITION_CITADEL_ASSETS.twinTowerRight.key)
      .setCrop(45, 105, 675, 875).setOrigin(382.5 / 768, 542.5 / 1024).setAlpha(0.88);
    this.leftLabel = this.makeText('TOWER 8', 32);
    this.rightLabel = this.makeText('TOWER 5', 32);
    this.equation = this.makeText('8 + 5', 38);
    this.instruction = this.makeText(
      'Move 2 glowing cubes from 5 to 8. Build a tower of 10!',
      21,
    );
    this.instruction.setWordWrapWidth(760).setAlign('center');

    this.hintButton = scene.add.image(1120, 590, ADDITION_CITADEL_ASSETS.hintButton.key)
      .setCrop(145, 320, 600, 430)
      .setOrigin(445 / 900, 535 / 766)
      .setInteractive({ useHandCursor: true });
    this.resetButton = scene.add.image(1120, 665, ADDITION_CITADEL_ASSETS.resetButton.key)
      .setCrop(110, 315, 620, 440)
      .setOrigin(420 / 900, 535 / 776)
      .setInteractive({ useHandCursor: true });
    this.hintLabel = this.makeText('HINT (H)', 15);
    this.resetLabel = this.makeText('RESET (R)', 15);
    this.transferZone = scene.add.zone(850, 420, 320, 390)
      .setInteractive({ useHandCursor: true });

    this.root.add([
      this.missionPanel,
      this.leftTower,
      this.rightTower,
      this.leftLabel,
      this.rightLabel,
      this.equation,
      this.instruction,
      this.hintButton,
      this.resetButton,
      this.hintLabel,
      this.resetLabel,
      this.transferZone,
    ]);

    this.transferZone.on('pointerdown', this.transferFromFive, this);
    this.hintButton.on('pointerdown', this.showHint, this);
    this.resetButton.on('pointerdown', this.reset, this);
    scene.input.keyboard?.on('keydown-ENTER', this.transferFromFive, this);
    scene.input.keyboard?.on('keydown-SPACE', this.transferFromFive, this);
    scene.input.keyboard?.on('keydown-H', this.showHint, this);
    scene.input.keyboard?.on('keydown-R', this.reset, this);
  }

  layout(safe: LogicalBounds, compact: boolean) {
    if (this.destroyed) return;
    if (this.moving) {
      this.unitPlacements.forEach(({ image }) => this.scene.tweens.killTweensOf(image));
      this.moving = false;
      this.controller.transfer('b');
    }

    this.safe = safe;
    this.compact = compact;
    this.controller.layout({
      left: safe.x,
      right: safe.right,
      top: safe.y,
      bottom: safe.bottom,
    });

    const centerX = safe.centerX;
    const towerGap = compact ? Math.min(350, safe.width * 0.34) : Math.min(430, safe.width * 0.36);
    const towerY = compact ? safe.centerY + 54 : safe.centerY + 70;
    const towerWidth = compact ? 235 : 292;
    const towerHeight = compact ? 345 : 405;
    const leftX = centerX - towerGap * 0.5;
    const rightX = centerX + towerGap * 0.5;

    const panelWidth = Math.min(safe.width * 0.78, compact ? 720 : 850);
    const panelHeight = compact ? 118 : 132;
    this.missionPanel.setPosition(centerX, safe.y + (compact ? 72 : 82))
      .setScale(panelWidth / 750, panelHeight / 215);
    this.equation.setPosition(centerX, safe.y + (compact ? 50 : 56))
      .setFontSize(compact ? 31 : 38);
    this.instruction.setPosition(centerX, safe.y + (compact ? 98 : 110))
      .setFontSize(compact ? 17 : 21)
      .setWordWrapWidth(Math.min(safe.width * 0.66, 760));

    this.leftTower.setPosition(leftX, towerY).setScale(towerWidth / 620, towerHeight / 850);
    this.rightTower.setPosition(rightX, towerY).setScale(towerWidth / 675, towerHeight / 875);
    this.leftLabel.setPosition(leftX, towerY - towerHeight * 0.39)
      .setFontSize(compact ? 23 : 29);
    this.rightLabel.setPosition(rightX, towerY - towerHeight * 0.39)
      .setFontSize(compact ? 23 : 29);

    const controlX = Math.min(safe.right - 55, rightX + towerWidth * 0.72);
    const hintY = safe.bottom - (compact ? 112 : 125);
    const resetY = safe.bottom - (compact ? 45 : 48);
    const controlWidth = compact ? 64 : 76;
    const controlHeight = compact ? 55 : 64;
    this.hintButton.setPosition(controlX, hintY).setScale(controlWidth / 600, controlHeight / 430);
    this.resetButton.setPosition(controlX, resetY).setScale(controlWidth / 620, controlHeight / 440);
    this.hintLabel.setPosition(controlX - (compact ? 58 : 69), hintY).setOrigin(1, 0.5)
      .setFontSize(compact ? 13 : 15);
    this.resetLabel.setPosition(controlX - (compact ? 58 : 69), resetY).setOrigin(1, 0.5)
      .setFontSize(compact ? 13 : 15);
    this.transferZone.setPosition(rightX, towerY + 10).setSize(towerWidth, towerHeight * 0.72);

    this.renderState(this.controller.snapshot);
  }

  setSuspended(suspended: boolean) {
    this.suspended = suspended;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.input.keyboard?.off('keydown-ENTER', this.transferFromFive, this);
    this.scene.input.keyboard?.off('keydown-SPACE', this.transferFromFive, this);
    this.scene.input.keyboard?.off('keydown-H', this.showHint, this);
    this.scene.input.keyboard?.off('keydown-R', this.reset, this);
    this.unitPlacements.forEach(({ image }) => this.scene.tweens.killTweensOf(image));
    this.unitPlacements.length = 0;
    this.root.destroy(true);
  }

  private readonly transferFromFive = () => {
    if (this.destroyed || this.suspended || this.moving || this.controller.snapshot.isComplete) return;
    const source = this.unitPlacements
      .filter(unit => unit.tower === 'b')
      .sort((a, b) => b.index - a.index)[0];
    if (!source) return;

    this.moving = true;
    audio.click();
    const destination = this.getUnitPosition('a', this.controller.snapshot.towerA);
    if (this.options.reducedMotion) {
      this.moving = false;
      this.controller.transfer('b');
      return;
    }

    source.image.disableInteractive().setDepth(80);
    this.scene.tweens.add({
      targets: source.image,
      x: destination.x,
      y: destination.y,
      scaleX: source.image.scaleX * 1.15,
      scaleY: source.image.scaleY * 1.15,
      duration: 420,
      ease: 'Cubic.easeInOut',
      onComplete: () => {
        if (this.destroyed) return;
        this.moving = false;
        audio.sparkle();
        this.controller.transfer('b');
      },
    });
  };

  private readonly showHint = () => {
    if (this.destroyed || this.suspended) return;
    audio.hint();
    const remaining = Math.max(0, this.controller.config.landmark - this.controller.snapshot.towerA);
    this.instruction.setText(
      remaining === 0
        ? 'You made 10! The 3 cubes stay in the other tower.'
        : `Move ${remaining} more glowing cube${remaining === 1 ? '' : 's'} from the tower on the right.`,
    );
  };

  private readonly reset = () => {
    if (this.destroyed || this.suspended || this.moving || this.controller.snapshot.isComplete) return;
    audio.click();
    this.controller.reset();
    this.instruction.setText('Move 2 glowing cubes from 5 to 8. Build a tower of 10!');
  };

  private renderState(snapshot: TwinTowerCombineSnapshot) {
    if (this.destroyed || !this.safe) return;
    this.unitPlacements.splice(0).forEach(({ image }) => image.destroy());
    this.createUnits('a', snapshot.towerA);
    this.createUnits('b', snapshot.towerB);
    this.leftLabel.setText(`TOWER ${snapshot.towerA}`);
    this.rightLabel.setText(`TOWER ${snapshot.towerB}`);
    this.equation.setText(snapshot.isComplete
      ? `${snapshot.recomposedAddends[0]} + ${snapshot.recomposedAddends[1]} = ${snapshot.total}`
      : `${snapshot.towerA} + ${snapshot.towerB}`);
    if (snapshot.isComplete) {
      this.instruction.setText('10 and 3 still make 13. The Citadel is powering up!');
      this.transferZone.disableInteractive();
      this.hintButton.disableInteractive();
      this.resetButton.disableInteractive();
    }
  }

  private createUnits(tower: TwinTowerId, count: number) {
    for (let index = 0; index < count; index += 1) {
      const position = this.getUnitPosition(tower, index);
      const image = this.scene.add.image(
        position.x,
        position.y,
        NUMBER_KINGDOM_ASSETS.oneCube.key,
      ).setDepth(55).setCrop(25, 42, 112, 122).setOrigin(81 / 160, 103 / 165).setScale(
        (this.compact ? 42 : 50) / 112,
        (this.compact ? 44 : 52) / 122,
      );
      if (tower === 'b') {
        image.setInteractive({ useHandCursor: true });
        image.on('pointerdown', this.transferFromFive, this);
      }
      this.unitPlacements.push({ image, tower, index });
      this.root.add(image);
    }
  }

  private getUnitPosition(tower: TwinTowerId, index: number) {
    const safe = this.safe!;
    const towerGap = this.compact ? Math.min(350, safe.width * 0.34) : Math.min(430, safe.width * 0.36);
    const centerX = safe.centerX + (tower === 'a' ? -towerGap * 0.5 : towerGap * 0.5);
    const towerY = this.compact ? safe.centerY + 54 : safe.centerY + 70;
    const columns = 2;
    const column = index % columns;
    const row = Math.floor(index / columns);
    const gapX = this.compact ? 48 : 58;
    const gapY = this.compact ? 47 : 54;
    return {
      x: centerX + (column - 0.5) * gapX,
      y: towerY + 116 - row * gapY,
    };
  }

  private makeText(text: string, fontSize: number) {
    const label = this.scene.add.text(0, 0, text, {
      fontFamily: FONT_UI,
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      align: 'center',
      color: '#fff8d8',
      stroke: '#32104f',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 2, blur: 4, color: '#160423', fill: true },
    }).setOrigin(0.5).setDepth(65);
    return label;
  }
}
